import { Link } from 'react-router'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, RefreshCw } from 'lucide-react'
import { api, type Item } from '../lib/api'
import { TYPE_ORDER, TYPES } from '../lib/types'
import { missingArabic } from '../lib/formValues'
import { changedSummary, usePublishStatus } from '../components/PublishButton'
import { Notice, PageHeader, Spinner, StatusChip, ago } from '../components/ui'

export default function Dashboard() {
  const status = usePublishStatus()
  const lists = useQueries({
    queries: TYPE_ORDER.map((t) => ({
      queryKey: ['content', t],
      queryFn: () => api.get<{ items: Item[] }>(`/content/${t}`),
    })),
  })

  return (
    <>
      <PageHeader title="Dashboard" />

      <section className="card mb-6 p-5">
        <h2 className="text-sm font-semibold">Site</h2>
        {status.data ? (
          <div className="mt-2 space-y-1 text-sm text-muted">
            <p>
              Last publish:{' '}
              {status.data.last ? (
                <>
                  <StatusChip status={status.data.last.status} /> {ago(status.data.last.createdAt)}
                  {status.data.last.triggeredBy && <> by {status.data.last.triggeredBy}</>}
                  {status.data.last.runUrl && (
                    <a className="ms-2 inline-flex items-center gap-1 text-accent" href={status.data.last.runUrl} target="_blank" rel="noreferrer">
                      build log <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </>
              ) : 'never from the CMS'}
            </p>
            <p>Changed since then: <b className="text-fg">{changedSummary(status.data.changed)}</b></p>
            <p className="text-xs text-faint">
              Edits to published items reach mlopsmena.com on the next publish (or the next code deploy).
            </p>
          </div>
        ) : <p className="mt-2 text-sm text-faint">Loading…</p>}
      </section>

      <CommunityFigures />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {TYPE_ORDER.map((t, i) => {
          const items = lists[i].data?.items ?? []
          const drafts = items.filter((x) => x.status === 'draft').length
          const untranslated = items.filter((x) => x.status === 'published' && missingArabic(x, TYPES[t].englishOnly).length > 0).length
          return (
            <Link key={t} to={`/content/${t}`} className="card p-4 transition hover:border-accent/50">
              <p className="text-sm font-semibold">{TYPES[t].label}</p>
              <p className="mt-1 text-2xl font-bold">{lists[i].isPending ? '…' : items.filter((x) => x.status === 'published').length}</p>
              <p className="text-xs text-faint">
                published{drafts > 0 && ` · ${drafts} draft${drafts > 1 ? 's' : ''}`}
                {untranslated > 0 && <span className="text-warn"> · {untranslated} need Arabic</span>}
              </p>
            </Link>
          )
        })}
      </div>
    </>
  )
}

const SOURCES = { youtube: 'YouTube', github: 'GitHub', discord: 'Discord', calendar: 'Google Calendar' } as const

/** The figures the Worker fetches every 3 hours. The site's browsers read them live, so a refresh needs no publish. */
function CommunityFigures() {
  const qc = useQueryClient()
  const stats = useQuery({
    queryKey: ['tools', 'stats'],
    queryFn: () => api.get<{ fetchedAt: Record<keyof typeof SOURCES, string | null> }>('/tools/stats'),
  })
  const refresh = useMutation({
    mutationFn: () => api.post<{ result: Record<keyof typeof SOURCES, 'updated' | 'kept'> }>('/tools/stats/refresh', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tools', 'stats'] }),
  })
  const kept = refresh.data && Object.entries(refresh.data.result).filter(([, r]) => r === 'kept').map(([s]) => SOURCES[s as keyof typeof SOURCES])

  return (
    <section className="card mb-6 p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Community figures and calendar</h2>
        <button type="button" className="btn" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
          {refresh.isPending ? <Spinner /> : <RefreshCw className="h-4 w-4" />} Refresh now
        </button>
      </div>
      <p className="mt-1 text-xs text-faint">
        Fetched every 3 hours. The site picks up new figures without a publish.
      </p>
      <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-4">
        {Object.entries(SOURCES).map(([key, label]) => (
          <div key={key}>
            <dt className="text-faint">{label}</dt>
            <dd>{stats.data ? ago(stats.data.fetchedAt[key as keyof typeof SOURCES]) : '…'}</dd>
          </div>
        ))}
      </dl>
      {refresh.isError && <div className="mt-3"><Notice>{(refresh.error as Error).message}</Notice></div>}
      {kept && kept.length > 0 && (
        <div className="mt-3"><Notice kind="warn">Could not reach {kept.join(', ')}. The last good copy stays on the site.</Notice></div>
      )}
    </section>
  )
}
